import { useContext, useEffect, useMemo, useState } from "react";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { TransactionsNavContext } from "./TransactionsScreen";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { InstitutionsContext } from "../firebaseio/InstitutionsContext";
import { BreadTransaction, Predicate } from "breadcommon";
import { P } from "breadcommon";
import { DropDownEntry, DropDownGroup } from "../rye/RyeDropDown";
import { TransactionColumn } from "./TransactionColumn";
import { buildClasses } from "../utils/buildClasses";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";
import { InstitutionLogo } from "../common/InstitutionLogo";

export function TransactionColumnFilterAccount({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const accounts = useContext(AccountsContext);
  const institutions = useContext(InstitutionsContext);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  useEffect(() => {
    if (!hasActiveFilter) {
      setSelectedAccountIds([]);
    }
  }, [hasActiveFilter]);

  function createAccountPredicate(accountIds: string[]): Predicate | null {
    if (accountIds.length === 0) {
      return null;
    }

    const field = P.Field<BreadTransaction, "account_id">("account_id");
    const predicates = accountIds.map((id) => P.Equals(field, P.Literal(id)));
    return P.Any(...predicates);
  }

  function applyFilter(accountIds: string[]) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.ACCOUNT];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.ACCOUNT]: {
          ...currentSettings,
          predicate: createAccountPredicate(accountIds),
        },
      },
    }));
  }

  // Create entries and groups for the dropdown
  const { entries, groups } = useMemo(() => {
    const institutionGroups: DropDownGroup[] = [];

    // Create institution groups
    Array.from(institutions.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((institution) => {
        institutionGroups.push({
          id: institution.id,
          label: institution.name,
        });
      });

    // Add "Other" group for accounts without an institution
    institutionGroups.push({
      id: "no_institution",
      label: "Other",
    });

    // Create account entries
    const accountEntries: DropDownEntry<string>[] = Array.from(
      accounts.values(),
    ).map((account) => {
      const institution = account.institution_id
        ? institutions.get(account.institution_id)
        : null;
      return {
        id: account.id,
        text: account.name,
        icon: (
          <div className={buildClasses("flex", "items-center")}>
            <InstitutionLogo institution={institution} size={18} />
          </div>
        ),
        groupId: account.institution_id || "no_institution",
      };
    });

    return {
      entries: accountEntries,
      groups: institutionGroups,
    };
  }, [accounts, institutions]);

  function handleSelectionChange(accountIds: string[]) {
    setSelectedAccountIds(accountIds);
    applyFilter(accountIds);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className={buildClasses("flex", "flex-col", "gap-2")}>
      <RyeDropDownMultiSelect
        selectedEntryIds={selectedAccountIds}
        onSelectionChange={handleSelectionChange}
        onEsc={onClose}
        entries={entries}
        groups={groups}
        searchable
        size="md"
        width="lg"
      />
    </div>
  );
}
